/**
 * The explanations behind every `?` marker and the Guide overlay.
 *
 * Tooltip text and documentation are the same objects, deliberately. Keeping
 * them apart guarantees they drift, and a hover bubble that contradicts the
 * guide is worse than no bubble at all: it teaches the reader not to trust
 * either. `short` is what fits in a bubble; `body` is the same idea with room
 * to justify itself.
 *
 * Content rule: say what the thing *is*, then what goes wrong if you get it
 * wrong. The second half is the part that actually changes behaviour — a
 * definition of "correlation group" nobody acts on is not documentation.
 */

import type { ReactNode } from 'react';

export interface Topic {
  /** Anchor id in the guide; also the value passed to `<Info topic="…" />`. */
  id: TopicId;
  term: string;
  /** One or two sentences. Shown in the hover bubble. */
  short: string;
  body: ReactNode;
}

export type TopicId =
  | 'variable'
  | 'distribution'
  | 'measured-data'
  | 'unit'
  | 'dimensions'
  | 'transfer-function'
  | 'intermediate'
  | 'spec-limits'
  | 'correlation'
  | 'correlation-group'
  | 'correlation-matrix'
  | 'worst-case'
  | 'rss'
  | 'monte-carlo'
  | 'iterations'
  | 'seed'
  | 'sigma'
  | 'yield'
  | 'ppm'
  | 'cp-cpk'
  | 'normality'
  | 'sensitivity'
  | 'tolerance-ranking';

const TOPIC_LIST: Topic[] = [
  {
    id: 'variable',
    term: 'Variables',
    short:
      'One varying quantity. It is drawn once per trial, and every expression that names it reads that same value — so how many you declare is what decides what varies independently.',
    body: (
      <>
        <p>
          A variable is one quantity that varies, with a distribution describing how. On each
          Monte Carlo trial it is drawn <strong>once</strong>, and every expression that names it
          reads that one value.
        </p>
        <p>
          That single rule is the whole model, and it means the only thing deciding what varies
          independently is <em>how many variables you declare</em>:
        </p>
        <ul>
          <li>
            A quantity shared by the whole assembly — ambient temperature, supply voltage, age —
            is <strong>one variable</strong>. Name it <code>T</code> in three different
            expressions and all three see the same temperature on every trial, because all the
            parts really do sit in the same oven. Its drift is common-mode: in a ratio it
            largely cancels, in a sum it accumulates.
          </li>
          <li>
            A property of an individual part is <strong>one variable per part</strong>. Three
            resistors are three parts that miss nominal in unrelated directions, so they need{' '}
            <code>R1</code>, <code>R2</code> and <code>R3</code> — three declarations, three
            independent draws.
          </li>
        </ul>
        <p className="guide-warn">
          The one modelling error worth guarding against is giving each part its own copy of an
          environmental quantity — writing <code>T1</code>, <code>T2</code>, <code>T3</code>{' '}
          where the hardware has one oven. Those draw independently, so a common-mode drift stops
          cancelling in a ratio and the answer flatters the design. Nothing in the engine can
          detect it, because a variable is a variable; it is a question you have to ask while
          writing the model.
        </p>
        <p>
          If two variables should vary together without being the same quantity — parts from one
          reel, features from one setup — that is what correlation is for, not a second copy of
          the variable.
        </p>
      </>
    ),
  },
  {
    id: 'distribution',
    term: 'Distribution',
    short:
      'How a variable varies around its nominal. The shape matters as much as the width — a uniform ±1% and a normal ±1% are different risks.',
    body: (
      <>
        <p>
          A tolerance on a datasheet is a <em>bound</em>, not a distribution. The shape you
          choose says what you believe about how the parts fill that bound, and it changes the
          answer:
        </p>
        <ul>
          <li>
            <strong>Uniform</strong> — anything within the band is equally likely. The honest
            default when all you have is a tolerance and no process knowledge. Pessimistic for
            most real processes, which is usually the right way to be wrong.
          </li>
          <li>
            <strong>Normal</strong> — a centred process with most parts near nominal. Only use
            it when you have grounds to; it is unbounded, so a small fraction of samples fall
            outside any band you might have imagined.
          </li>
          <li>
            <strong>Truncated normal</strong> — a normal process that has been{' '}
            <em>100% screened</em>, with out-of-band parts removed. This is what a tested,
            graded component actually looks like.
          </li>
          <li>
            <strong>Triangular</strong> — a peak at a most-likely value with hard limits. Useful
            for expert estimates: minimum, most likely, maximum.
          </li>
          <li>
            <strong>Lognormal</strong> — strictly positive and right-skewed. Fits quantities
            that multiply rather than add, such as wear and lifetime.
          </li>
          <li>
            <strong>Discrete</strong> — a fixed set of values with weights, for anything that
            comes in steps rather than a continuum.
          </li>
          <li>
            <strong>Fixed</strong> — a constant. Zero variance, still participates in the
            transfer function.
          </li>
          <li>
            <strong>Empirical</strong> — your own measured samples, used directly. See{' '}
            <em>Measured data</em>.
          </li>
        </ul>
        <p>
          The ± shorthand buttons write a uniform or normal band around the current nominal, so
          the common case is one click rather than arithmetic.
        </p>
      </>
    ),
  },
  {
    id: 'measured-data',
    term: 'Measured data',
    short:
      'Import a column of real measurements and use them as the distribution directly, instead of guessing a shape.',
    body: (
      <>
        <p>
          Paste or upload a CSV column and the values become an{' '}
          <strong>empirical distribution</strong>, sampled by inverse transform from your actual
          data. No shape is assumed and no curve is fitted, so whatever skew, truncation or
          bimodality your process has is carried straight into the analysis.
        </p>
        <p>
          The importer is deliberately forgiving about format — it detects the delimiter,
          handles European decimal commas, strips unit suffixes and byte-order marks — and
          deliberately strict about content. Cells it cannot read are <em>reported</em>, never
          silently dropped. Quietly discarding unreadable rows would bias the distribution
          toward whatever happened to be machine-readable, which is exactly the kind of error
          that never announces itself.
        </p>
        <p>
          Measured data is the strongest input this tool accepts. Prefer it over a guessed shape
          whenever you have it.
        </p>
      </>
    ),
  },
  {
    id: 'unit',
    term: 'Units',
    short:
      'Free text, carried through to every result and chart. Prefixed SI units like mV or mm are understood and normalised — 5000 mV displays as 5.000 V.',
    body: (
      <>
        <p>
          Units are labels; the tool does no dimensional analysis and will not stop you adding
          volts to millimetres. What it does do is read the unit when formatting, so a value of
          5000 with unit <code>mV</code> is shown as <code>5.000 V</code> rather than{' '}
          <code>5.000 kmV</code>.
        </p>
        <p>
          A unit that resolves to an SI prefix on a known base — <code>mV</code>, <code>mm</code>
          , <code>kΩ</code>, <code>MHz</code>, <code>µF</code>, <code>daN</code> — is normalised
          to its base and then given exactly one prefix, chosen by magnitude. The displayed
          quantity is always the value you entered; only its spelling changes.
        </p>
        <p>
          Anything else — <code>°C</code>, <code>%</code>, <code>psi</code>, <code>dB</code>,{' '}
          <code>counts</code> — is printed exactly as typed and never given a prefix, because{' '}
          <code>m°C</code> helps nobody.
        </p>
        <p>
          Alternative spellings resolve to one unit and display canonically: <code>ohm</code>,{' '}
          <code>Ohm</code>, <code>OHM</code> and <code>ohms</code> are all <code>Ω</code>, so a
          model that mixes them is consistent both in its arithmetic checks and on screen.
          Matching is per spelling rather than case-insensitive, because unit symbols are
          case-sensitive — <code>m</code> and <code>M</code>, <code>s</code> and <code>S</code>{' '}
          are different things.
        </p>
      </>
    ),
  },
  {
    id: 'dimensions',
    term: 'Dimensional analysis',
    short:
      'Checks your transfer function for unit errors — a length added to a time, an output declared in volts whose expression yields watts. Advisory, and silent on anything it cannot read confidently.',
    body: (
      <>
        <p>
          Whenever the model changes, every expression is walked and its units are worked out
          from the units of the variables it references. Two kinds of error surface:
        </p>
        <ul>
          <li>
            <strong>Dimension mismatch</strong> — adding a length to a time, or declaring an
            output in <code>A</code> when the expression evaluates to{' '}
            <code>V·Ω</code>. These are almost always a genuine mistake in the model.
          </li>
          <li>
            <strong>Scale mismatch</strong> — subtracting a value in <code>m</code> from one in{' '}
            <code>mm</code>. Both are lengths, so a dimension-only check passes it, but the
            arithmetic is still wrong: this tool evaluates the numbers you typed and{' '}
            <em>never converts between units</em>. The warning tells you the factor involved.
          </li>
        </ul>
        <p>
          Units compose, so <code>V^2 / Ω</code> is recognised as watts without anything
          declaring watts along the way. Compound unit strings work too — <code>1/°C</code>,{' '}
          <code>kg*m/s^2</code>, <code>N·m</code>, <code>m²</code>.
        </p>
        <p className="guide-warn">
          The checker never guesses. A unit it does not recognise — <code>%</code>,{' '}
          <code>psi</code>, <code>pcs</code>, anything domain-specific — makes that quantity
          unknown, and every expression touching it is skipped rather than assumed. The same
          goes for a variable with no unit at all. That is why a model can be silently
          unchecked, and why the variables section reports how much of it the checker can
          actually read. The alternative is a tool that cries wolf on ordinary text, which
          gets switched off and then protects nobody.
        </p>
        <p>
          Findings are warnings, never blocks. A unit label is documentation; being wrong about
          one does not make the arithmetic you asked for un-runnable, and you may well be
          modelling something the tool has no physics for.
        </p>
        <p>
          One deliberate simplification: <code>°C</code> is treated as a temperature with no
          offset. Every use of temperature in a transfer function here is a difference —{' '}
          <code>tcr * (T - 25)</code> — where the offset cancels and °C and K behave
          identically.
        </p>
      </>
    ),
  },
  {
    id: 'transfer-function',
    term: 'Transfer function',
    short:
      'The expression that turns variables into an output. Written in ordinary algebra: Vin * R2 / (R1 + R2).',
    body: (
      <>
        <p>
          An output is an expression over your variables and intermediates, in ordinary infix
          notation. Operators <code>+ - * / ^</code>, parentheses, and the usual functions —{' '}
          <code>sqrt</code>, <code>abs</code>, <code>min</code>, <code>max</code>,{' '}
          <code>exp</code>, <code>log</code>, <code>sin</code>, <code>cos</code>,{' '}
          <code>tan</code>, and the constants <code>pi</code> and <code>e</code>.
        </p>
        <p>
          Expressions are parsed to a syntax tree and compiled to a function. They are never
          evaluated as code, which is what makes opening a project file someone emailed you safe
          rather than an act of trust.
        </p>
        <p>
          The same tree is differentiated symbolically to produce the sensitivities that RSS and
          the worst-case contributor table need — which is why those methods can tell you{' '}
          <em>∂out/∂var</em> exactly rather than estimating it.
        </p>
      </>
    ),
  },
  {
    id: 'intermediate',
    term: 'Intermediates',
    short:
      'Named sub-expressions. They keep a long transfer function readable and let several outputs share a common term.',
    body: (
      <>
        <p>
          An intermediate is a named expression that other expressions can reference. It is not
          a variable: it has no distribution of its own, it is simply computed from whatever it
          references on each trial.
        </p>
        <p>
          Use them to name the physical quantities in the middle of a chain — an effective
          resistance after temperature drift, a stack height before a clearance is taken. The
          dependency graph is resolved and ordered when the model is built, and a reference to
          something undefined, or a cycle, is reported before anything runs.
        </p>
      </>
    ),
  },
  {
    id: 'spec-limits',
    term: 'Spec limits (LSL / USL)',
    short:
      'The lower and upper bounds the output must stay inside. They are what make yield, PPM and Cp/Cpk computable.',
    body: (
      <>
        <p>
          <strong>LSL</strong> and <strong>USL</strong> are the acceptance limits for the output
          — the point at which a unit is scrap or rework. Either may be left empty for a
          one-sided specification.
        </p>
        <p>
          Without limits, the analysis still gives you a mean, a σ and a distribution, but yield,
          PPM and the capability indices have nothing to be measured against and are omitted.
          The optional <strong>target</strong> is the value you are aiming at, which need not be
          the midpoint of the limits.
        </p>
        <p>
          Out-of-limit samples are counted exactly during the run, not read off the histogram, so
          the yield figure does not inherit the histogram's bin width.
        </p>
      </>
    ),
  },
  {
    id: 'correlation',
    term: 'Correlation',
    short:
      'Whether two variables miss their nominal together. Assuming independence when parts actually track each other is the classic way to get a confidently wrong answer.',
    body: (
      <>
        <p>
          By default every variable is independent: knowing that one part came out high tells
          you nothing about the next. Real parts often violate this. Two resistors from the same
          reel were made in the same run from the same material, and they are high or low{' '}
          <em>together</em>.
        </p>
        <p>
          Correlation is expressed as ρ, between −1 and 1. ρ = 0 is independent, ρ = 1 is
          perfectly matched, ρ = −1 is perfectly opposed. It changes the answer in both
          directions, and which direction depends on your transfer function:
        </p>
        <ul>
          <li>
            In a <strong>ratio or difference</strong> — a divider, a clearance between two
            surfaces — positive correlation makes the output <em>tighter</em>. Both sides move
            together and the error cancels. A matched pair at ρ = 1 drives output variance to
            zero.
          </li>
          <li>
            In a <strong>sum</strong> — a stack-up of parts from one lot — positive correlation
            makes the output <em>wider</em>. The errors reinforce instead of averaging out.
          </li>
        </ul>
        <p className="guide-warn">
          So there is no safe default. Assuming independence is optimistic for a stack-up and
          pessimistic for a matched pair, and in both cases the size of the error grows with the
          number of correlated parts.
        </p>
        <p>
          Under the hood, correlation is applied through a Gaussian copula: the underlying
          normals are correlated and then fed back through each variable's own inverse CDF, so
          each variable keeps exactly the marginal distribution you specified. Making a pair
          correlated never quietly changes either one's shape.
        </p>
      </>
    ),
  },
  {
    id: 'correlation-group',
    term: 'Correlation group',
    short:
      'A named set of variables that all track each other equally, at one shared ρ. The right tool for "these came from the same reel".',
    body: (
      <>
        <p>
          A group gives every member the same pairwise correlation ρ with every other member —
          equicorrelation. One slider covers the whole set, which matches how the physical cause
          usually works: a lot, a reel, a batch, a single machining setup.
        </p>
        <p>
          ρ = 0.8 is a reasonable starting point for same-reel passives. ρ = 1 models a{' '}
          <em>matched set</em>, parts explicitly selected against each other — and is worth
          trying just to see how much of your output spread the mismatch is responsible for.
        </p>
        <p>
          If you need structure a single ρ cannot express — negative correlation, or A
          correlated with B but not with C — use the explicit correlation matrix instead. A
          variable may be in a group or in the matrix, but not both: the two mechanisms would
          otherwise both be trying to drive the same underlying draw.
        </p>
      </>
    ),
  },
  {
    id: 'correlation-matrix',
    term: 'Explicit correlations',
    short:
      'Pairwise ρ between named variables, for structure a single group cannot express. Validated for consistency, since not every set of pairwise numbers is physically possible.',
    body: (
      <>
        <p>
          Set ρ per pair when a group's single shared value is too blunt: negative correlation, a
          chain where A tracks B and B tracks C but A and C are unrelated, or different strengths
          between different pairs.
        </p>
        <p>
          Not every table of pairwise numbers describes a possible world. Asking for ρ(A,B) =
          0.9, ρ(B,C) = 0.9 and ρ(A,C) = −0.9 is a contradiction — if A and B move together and B
          and C move together, A and C cannot move oppositely. Mathematically the matrix must be
          positive semi-definite, and one that is not is rejected with an explanation rather than
          quietly producing numbers.
        </p>
        <p>
          When that happens you can accept the <strong>nearest valid</strong> matrix, which
          reports how far it had to move your values. A large correction is information: it means
          your assumptions disagreed with each other by that much.
        </p>
      </>
    ),
  },
  {
    id: 'worst-case',
    term: 'Worst case (WCA)',
    short:
      'Every tolerance at its most damaging extreme simultaneously. A guarantee, not a prediction — and usually far wider than anything you will build.',
    body: (
      <>
        <p>
          Worst case puts every variable at whichever end of its range pushes the output furthest,
          all at once. If the result is inside spec you need no statistics: the part cannot fail
          on tolerances. That is a genuine guarantee and it is why the method survives.
        </p>
        <p>
          It is also enormously pessimistic. The probability of twenty independent variables all
          landing at the same extreme corner is not small, it is negligible — designing to
          worst-case on a long stack-up means paying for tolerances no unit will ever need.
        </p>
        <p>
          Two details this tool does not hand-wave. First, the corner is only the true extreme if
          the output is <strong>monotonic</strong> in each variable; for something like{' '}
          <code>x·(1−x)</code> the peak is in the middle, and the corners miss it. Monotonicity is
          therefore <em>verified</em>, and where it fails you get a guaranteed interval-arithmetic
          bound alongside the corner result, plus a warning. Second, unbounded distributions
          (normal, lognormal) have no extreme, so they are clamped at ±3σ and the affected rows
          are tagged — which means the WCA range is not absolute when any variable is clamped.
        </p>
      </>
    ),
  },
  {
    id: 'rss',
    term: 'RSS',
    short:
      'Root-sum-square: combines the tolerances in quadrature. Fast and analytic, exact for a linear transfer function, approximate otherwise.',
    body: (
      <>
        <p>
          RSS treats each variable's contribution as ∂out/∂var × σ<sub>var</sub> and combines them
          in quadrature — the square root of the sum of squares — rather than by simple addition.
          That is the statistically correct combination for independent contributions, and it is
          typically three to five times tighter than worst case.
        </p>
        <p>
          The derivatives are symbolic, taken from the same expression tree you typed, so they are
          exact rather than numerically estimated. Correlation cross terms are included, so a
          correlated pair is handled correctly and not silently treated as independent.
        </p>
        <p>
          The assumption RSS makes is <strong>linearity</strong>: that the transfer function is
          well approximated by its slope over the tolerance range. When it is not, the answer is
          wrong and nothing about the number itself reveals that. This is why the tool always runs
          Monte Carlo too, and warns you when the two σ values disagree by more than 5% — at that
          point trust Monte Carlo and read the RSS contributor table as indicative only.
        </p>
      </>
    ),
  },
  {
    id: 'monte-carlo',
    term: 'Monte Carlo',
    short:
      'Simulate the whole assembly many times with randomly drawn variables and look at the distribution of results. The method with the fewest assumptions.',
    body: (
      <>
        <p>
          Draw every variable from its distribution, evaluate the transfer function, record the
          result, and repeat a million times. What comes out is the actual output distribution,
          with no assumption of linearity, no assumption of normality, and no assumption that
          extremes coincide.
        </p>
        <p>
          It handles what the other two cannot: nonlinear transfer functions, mixed and skewed
          distributions, correlation of any structure, and multiple outputs judged
          simultaneously. When the three methods disagree, this is the one to believe.
        </p>
        <p>
          Its cost is that it is a <em>sample</em>. Results carry sampling error, and rare events
          need a lot of trials before they are visible at all — see <em>Iterations</em>. Samples
          are accumulated in a streaming fashion, so memory stays constant no matter how many
          trials you run, and out-of-limit counts are kept exactly rather than being read off the
          histogram.
        </p>
      </>
    ),
  },
  {
    id: 'iterations',
    term: 'Iterations',
    short:
      'How many trials to simulate. More trials means less sampling noise, and it is the only way to resolve a rare failure.',
    body: (
      <>
        <p>
          Sampling error on a mean falls as 1/√N, so ten times the trials buys about three times
          the precision. That is a poor exchange rate, which is why the useful question is not
          "how accurate" but "what am I trying to see".
        </p>
        <ul>
          <li>
            <strong>10 000</strong> — instant. Fine while you are still editing the model and
            watching the shape move.
          </li>
          <li>
            <strong>100 000</strong> — the working default. Solid σ, yield to roughly three
            decimal places.
          </li>
          <li>
            <strong>1 000 000</strong> — a reportable number. Resolves defect rates down to a few
            PPM.
          </li>
          <li>
            <strong>10 000 000</strong> — for sub-PPM work, where you are counting a handful of
            failures.
          </li>
        </ul>
        <p>
          A defect rate cannot be estimated from failures you never observed. Below about ten
          observed failures the tool reports a bound rather than a point estimate, because
          "0 failures in 100 000" honestly means "below roughly 30 PPM", not "zero". The yield
          figure always carries a Wilson confidence interval so the sampling uncertainty is
          visible rather than implied.
        </p>
      </>
    ),
  },
  {
    id: 'seed',
    term: 'Seed',
    short:
      'The starting point for the random draws. The same project, seed and iteration count always produce identical results — on any machine, at any worker count.',
    body: (
      <>
        <p>
          The seed makes a run reproducible. It is stored in the project file and printed in the
          report footer alongside the iteration count and engine version, because an analysis
          nobody else can re-run is not an engineering document.
        </p>
        <p>
          Reproducibility here is bit-exact, not approximate, and it holds regardless of how many
          worker threads your machine used. That takes more than a seeded generator: floating
          point addition is not associative, so merging results in a different order changes the
          last bits even when every sample is identical. The run is therefore split into blocks
          whose count depends only on the iteration count, and those blocks are combined through a
          fixed tree — so the arithmetic is a property of the job, not of the hardware.
        </p>
        <p>
          Practical use: change the seed and re-run to see how much of a difference between two
          designs is real and how much is sampling noise. The tolerance ranking does this for you
          automatically — every probe run shares the baseline's seed, so a difference it reports
          is the tolerance change and not the dice.
        </p>
      </>
    ),
  },
  {
    id: 'sigma',
    term: 'Standard deviation (σ)',
    short:
      'The spread of the output. ±3σ covers about 99.73% of units when the output is normal — but only when it is.',
    body: (
      <>
        <p>
          σ summarises how widely the output varies. It is the single most useful number for
          comparing two design options, because it is unaffected by where the spec limits happen
          to sit.
        </p>
        <p>
          The familiar ±3σ ≈ 99.73% rule assumes a normal output. Sums of many comparable
          contributors do tend that way, but a dominant single contributor, a strongly nonlinear
          transfer function or a skewed input will all break it. The reported yield is counted
          from the samples themselves and does not rely on that assumption; the ±3σ figure does.
          Check the normality note before leaning on it.
        </p>
      </>
    ),
  },
  {
    id: 'yield',
    term: 'Yield',
    short:
      'The fraction of simulated units inside spec, with a confidence interval reflecting how many trials it was measured from.',
    body: (
      <>
        <p>
          Yield is counted exactly: every trial is tested against the limits as it is generated,
          so the figure does not depend on histogram binning. With several outputs you also get a{' '}
          <strong>system yield</strong> — the fraction of trials where <em>every</em> output was
          simultaneously in spec, computed per sample.
        </p>
        <p className="guide-warn">
          System yield is not the product of the individual yields. That shortcut is only valid
          when the outputs are independent, and outputs sharing variables never are. Multiplying
          them can be wrong in either direction.
        </p>
        <p>
          The interval quoted is a Wilson score interval, which stays sensible near 100% where the
          textbook normal-approximation interval famously does not — it would happily give you an
          upper bound above 1.
        </p>
      </>
    ),
  },
  {
    id: 'ppm',
    term: 'PPM',
    short:
      'Defects per million units — the same information as yield, at the resolution that matters when yield reads 99.99%.',
    body: (
      <>
        <p>
          Once yield is above about 99.9%, the interesting digits are all after the decimal point.
          PPM moves them where you can read them: 99.9997% yield is 3 PPM, and the difference
          between 3 PPM and 30 PPM is a real engineering distinction that "99.99%" versus "100%"
          conceals.
        </p>
        <p>
          A PPM figure is only as good as the number of failures it was computed from. If the run
          produced fewer than ten failures the value is reported as an upper bound, because
          extrapolating a rate from three observations is not measurement. To resolve single-digit
          PPM honestly you need on the order of 10<sup>7</sup> trials.
        </p>
      </>
    ),
  },
  {
    id: 'cp-cpk',
    term: 'Cp and Cpk',
    short:
      'Process capability. Cp is how wide the spec is relative to the spread; Cpk also penalises being off-centre. Both assume a normal output.',
    body: (
      <>
        <p>
          <strong>Cp</strong> compares the specification width to the process width: (USL − LSL) /
          6σ. It asks whether the spread <em>could</em> fit, ignoring where it actually sits.
        </p>
        <p>
          <strong>Cpk</strong> measures the distance from the mean to the nearer limit in units of
          3σ, so it captures both spread and centring. Cpk equals Cp only when the output is
          perfectly centred, and is always the smaller of the two.
        </p>
        <p>
          Conventional readings: Cpk ≥ 1.33 is a capable process, ≥ 1.67 is comfortable, and 2.0
          is the "six sigma" target. Cpk below 1.0 means the spread does not fit inside the limits.
        </p>
        <p className="guide-warn">
          Both indices are defined in terms of σ and assume the output is normal. When it is not,
          they can look reassuring while the real defect rate is far worse — the tail is where the
          failures are, and that is exactly where a non-normal distribution departs from the
          assumption. The tool runs a normality test and flags the indices when it fails; take the
          empirical yield instead in that case.
        </p>
      </>
    ),
  },
  {
    id: 'normality',
    term: 'Normality test',
    short:
      'Checks whether the output is close enough to normal for Cp/Cpk to mean anything. It warns only when the departure is big enough to move a number you would act on.',
    body: (
      <>
        <p>
          The test is D'Agostino–Pearson K², which combines the sample's skewness and kurtosis
          into a single statistic. But a p-value on its own is not a usable trigger here, and
          this is worth understanding because it is a trap the tool used to fall into.
        </p>
        <p>
          Statistical significance scales with sample size. At 100,000 iterations the standard
          error on excess kurtosis is about 0.015, so a kurtosis of 0.04 — a distribution that is
          normal to three decimal places — is a 2.6σ result and rejects normality. Acting on that
          would mean telling you to distrust Cp/Cpk on an output that is, for every practical
          purpose, perfectly normal.
        </p>
        <p>
          So the warning is gated on <strong>effect size</strong> instead: how far the measured
          skew and kurtosis move the ±3σ point, via a Cornish–Fisher expansion. Below 0.1σ —
          roughly 3% of a 3σ limit, less than the uncertainty in the tolerances that fed it —
          nothing is reported. The warning tells you the shift it found, so you can judge it.
        </p>
        <p>
          A warning here is not a problem with your design. Plenty of good systems have skewed
          outputs, especially with a nonlinear transfer function or a dominant asymmetric input.
          It is a statement about which numbers on the page you may rely on: yield and PPM are
          counted from samples and stay valid, while Cp, Cpk and the ±3σ range are derived
          assuming normality and do not.
        </p>
        <p>
          Look at the histogram as well. The question that matters is whether the <em>tails</em>{' '}
          near your limits behave, not whether the centre is textbook.
        </p>
      </>
    ),
  },
  {
    id: 'sensitivity',
    term: 'Sensitivity and variance share',
    short:
      'Which variables drive the output spread. Variance share attributes the σ; Spearman and SRC show the strength and direction of each influence.',
    body: (
      <>
        <p>
          <strong>Variance share</strong> is the headline: the percentage of output variance
          attributable to each variable. Shares are the thing to sort by, and they are usually
          brutally uneven — a handful of variables typically own most of the spread while the rest
          are noise.
        </p>
        <p>
          <strong>Spearman rank correlation</strong> measures how consistently the output moves
          with a variable, using ranks rather than values. Because it is rank-based it detects any
          monotonic relationship, not only a straight-line one. The sign tells you the direction.
        </p>
        <p>
          <strong>SRC</strong> (standardised regression coefficient) is the variable's slope
          expressed in standard deviations, so the values are directly comparable across variables
          with different units. SRCs are only trustworthy while a linear model explains most of
          the variance; when the reported R² drops below about 0.7 the system has interactions
          the SRC column cannot see, and the tool says so.
        </p>
        <p>
          All of this answers "where does the spread come from". It does not answer "what should I
          change" — for that, see the tolerance ranking.
        </p>
      </>
    ),
  },
  {
    id: 'tolerance-ranking',
    term: 'Tolerance ranking',
    short:
      'Halves each tolerance in turn, re-runs, and reports what you actually gain in σ and yield. The answer to "which tolerance is worth paying for".',
    body: (
      <>
        <p>
          Variance share tells you where the spread comes from. That is not the same question as
          which tolerance to buy. The two diverge whenever a large contributor is already as tight
          as your process can hold, or when a modest contributor sits right against a limit so
          that a small improvement moves the yield a lot.
        </p>
        <p>
          So this does the honest thing: it scales one variable's spread, re-runs the analysis, and
          reports the <em>measured</em> change in σ and in yield. Tighten halves the tolerance;
          loosen doubles it — and loosening is worth checking, because a tolerance you can relax
          for free is money.
        </p>
        <p>
          Every probe shares the baseline's seed, so any difference you see is the tolerance change
          and not sampling noise. Probe runs use fewer iterations than the headline run, since
          ranking needs far less precision than the reported number does.
        </p>
        <p>
          An analytic RSS prediction is shown alongside each measured result. Where the two
          disagree, the system is not behaving linearly over that range and the measured column is
          the one to trust.
        </p>
      </>
    ),
  },
];

export const TOPICS: Record<TopicId, Topic> = Object.fromEntries(
  TOPIC_LIST.map((t) => [t.id, t]),
) as Record<TopicId, Topic>;

export interface GuideSection {
  id: string;
  title: string;
  intro: ReactNode;
  topics: TopicId[];
}

export const GUIDE_SECTIONS: GuideSection[] = [
  {
    id: 'building',
    title: 'Building a model',
    intro: (
      <p>
        A model is a set of <strong>variables</strong> that vary, and one or more{' '}
        <strong>outputs</strong> computed from them. Give each variable a distribution describing
        how it varies, write the transfer function, set the limits the output must meet, and run.
      </p>
    ),
    topics: [
      'variable',
      'distribution',
      'measured-data',
      'unit',
      'dimensions',
      'transfer-function',
      'intermediate',
      'spec-limits',
    ],
  },
  {
    id: 'correlation',
    title: 'Correlation',
    intro: (
      <p>
        Independence is the default and it is an assumption, not a neutral choice. Parts from one
        reel, features cut in one setup, and readings from one instrument all move together — and
        whether that helps or hurts depends on your transfer function.
      </p>
    ),
    topics: ['correlation', 'correlation-group', 'correlation-matrix'],
  },
  {
    id: 'methods',
    title: 'The three methods',
    intro: (
      <p>
        Every run computes all three, side by side, because the interesting information is in where
        they disagree. Worst case gives a guarantee, RSS gives a fast analytic estimate, and Monte
        Carlo gives the distribution itself. Agreement means the system is linear and your
        tolerances are behaving; disagreement is the signal to look closer.
      </p>
    ),
    topics: ['worst-case', 'rss', 'monte-carlo', 'iterations', 'seed'],
  },
  {
    id: 'results',
    title: 'Reading the results',
    intro: (
      <p>
        Numbers here come with their caveats attached rather than in a footnote: a defect rate the
        sample size cannot resolve is shown as a bound, and capability indices carry a visible
        warning whenever the output fails a normality test. A confidently wrong number is worse
        than an honest range.
      </p>
    ),
    topics: ['sigma', 'yield', 'ppm', 'cp-cpk', 'normality', 'sensitivity', 'tolerance-ranking'],
  },
];
